import styled from '@emotion/styled'
import { Logo as LogoIcon } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import useMediaQuery from 'app/utils/useMediaQuery'
import { circOut, motion } from 'framer-motion'
import { Link } from 'gatsby'
import React, { memo } from 'react'

import { Contacts } from './Contacts'
import { MenuItem, Props as MenuItemProps } from './MenuItem'
import { SecondaryMenu } from './SecondaryMenu'
import { Props as SecondaryMenuItemProps } from './SecondaryMenuItem'

export interface Props {
  languagePrefix?: string | null
  menuItems: MenuItemProps[]
  modalStatus?: boolean
  secondaryMenuItems: SecondaryMenuItemProps[]
  siteName?: string
  startAnimation?: boolean
}

export const Navigation = memo(function Navigation({
  languagePrefix,
  menuItems,
  modalStatus,
  secondaryMenuItems,
  siteName,
  startAnimation,
}: Props) {
  if (!menuItems) {
    return null
  }

  const isMobile = useMediaQuery('(max-width: 1199px)')

  const variants = {
    visible: (i: any) => ({
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        bounce: 0,
        delay: i * 0.1,
        duration: 1,
        ease: circOut,
        type: 'spring',
      },
    }),
    hidden: {
      opacity: 0,
      x: isMobile ? 0 : -40,
      y: isMobile ? 30 : 0,
      transition: {
        delay: 0,
        duration: 0,
      },
    },
  }

  return (
    <Container className={modalStatus ? 'open' : undefined} dial={4}>
      <Logo
        aria-label={siteName}
        to={languagePrefix ? `/${languagePrefix}` : '/'}
      >
        <LogoIcon />
      </Logo>

      <Wrapper row stretch>
        <LeftSide dial={4}>
          <Nav>
            <NavList>
              {menuItems.slice(1).map((item, index) => (
                <NavLi
                  key={index}
                  custom={index}
                  animate={modalStatus ? 'visible' : 'hidden'}
                  variants={variants}
                >
                  <MenuItem modalStatus={modalStatus} {...item} />
                </NavLi>
              ))}
            </NavList>
          </Nav>
        </LeftSide>

        <RightSide space="between">
          {secondaryMenuItems ? (
            <SecondaryMenu
              menuItems={secondaryMenuItems}
              modalStatus={modalStatus}
              startAnimation={startAnimation}
            />
          ) : null}

          <Contacts modalStatus={modalStatus} startAnimation={startAnimation} />
        </RightSide>
      </Wrapper>
    </Container>
  )
})

const Logo = styled(Link)`
  width: 4.9375rem;
  height: 4.375rem;
  opacity: 0;
  position: absolute;
  top: 2.4375rem;
  left: 50%;
  transform: translate(-50%, -1.875rem);
  z-index: 2;

  > svg {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  &.open {
    ${Logo} {
      opacity: 1;
      transform: translate(-50%, 0);
      transition: 0.4s 0.2s ease-in-out;
    }
  }
`

const Wrapper = styled(FlexBox)`
  width: 100%;

  @media (max-width: 1199px) {
    display: block;
    height: 100%;
    overflow-y: auto;
    padding: 7.5rem 1.875rem 3.75rem;
    -webkit-overflow-scrolling: auto;
  }
`

const LeftSide = styled(FlexBox)`
  width: 66%;
  padding: 0 5.417vw;

  @media (max-width: 1199px) {
    width: 100%;
    justify-content: flex-start;
    padding: 0;
  }
`

const Nav = styled.nav``

const NavList = styled.ul``

const NavLi = styled(motion.li)`
  margin-top: 3rem;
  &:first-of-type {
    margin-top: 0;
  }

  @media (max-width: 1199px) {
    margin-top: 1.875rem;
    &:first-of-type {
      margin-top: 0;
    }
  }
`

const RightSide = styled(FlexBox)`
  width: 34%;

  @media (max-width: 1199px) {
    width: 100%;
  }
`
